import { ArrowBack, Send, SupportAgent, WavingHand } from '@mui/icons-material'
import { Button, IconButton, InputAdornment, TextField, Typography } from '@mui/material'

import { cx } from 'class-variance-authority'
import { ScrollAnchor } from 'components/scroll-anchor'
import { ChangeEvent, KeyboardEvent, memo, useCallback, useEffect, useState } from 'react'
import styles from './ai-chat.module.css'
import { ChatMessage } from './use-chat-sessions'

const typingMessage = (
	<div className={styles['typing-bubble']}>
		<span />
		<span />
		<span />
	</div>
)

const ChatMessages = memo(({ messages }: { messages: ChatMessage[] }) => {
	const lastMessage = messages.length ? messages.at(-1) : null

	return (
		<div className={styles['messages']}>
			{messages.map((message) => (
				<div className={styles['message']} key={message.id}>
					<span
						className={cx(styles['message-content'], {
							[styles['assistant']]: message.role === 'assistant',
							[styles['user']]: message.role === 'user',
							[styles['message-tail']]: message.isComplete,
						})}
					>
						{message.content} {!message.isComplete && typingMessage}
					</span>
				</div>
			))}
			<ScrollAnchor watchedContent={lastMessage?.content} />
		</div>
	)
})

ChatMessages.displayName = 'ChatMessages'

const AiChat = ({
	messages,
	onSendMessage,
	onBack,
	isLoading,
	sessionId,
}: {
	isLoading: boolean
	messages: ChatMessage[]
	onBack: () => void
	onSendMessage: (text: string) => void
	sessionId: string
}) => {
	const [prompt, setPrompt] = useState('')

	useEffect(() => {
		setPrompt('')
	}, [sessionId])

	const submitPrompt = useCallback(() => {
		if (!prompt || isLoading) {
			return
		}

		onSendMessage(prompt)
		setPrompt('')
	}, [isLoading, onSendMessage, prompt])

	const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
		if (e.shiftKey || e.key !== 'Enter') {
			return
		}

		submitPrompt()

		e.preventDefault()
		e.stopPropagation()
	}

	const onPromptChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		setPrompt(e.target.value)
	}

	return (
		<div className={styles['ai-container']}>
			<div className={styles['header']}>
				<div className={styles['header-content']}>
					<SupportAgent className={styles['header-icon']} />
					<Typography variant='h6' className={styles['header-title']}>
						Hello <WavingHand className={styles['wave-icon']} />
					</Typography>
				</div>
				<IconButton onClick={onBack} className={styles['back-btn']} size='small'>
					<Typography variant='h3' className={styles['header-title']}>
						<ArrowBack />
						Back
					</Typography>
				</IconButton>
			</div>
			<ChatMessages messages={messages} />
			<TextField
				className={styles['user-prompt']}
				variant='outlined'
				value={prompt}
				focused={true}
				autoFocus
				multiline={true}
				maxRows={7}
				onChange={onPromptChange}
				onKeyDown={onKeyDown}
				InputProps={{
					endAdornment: (
						<InputAdornment position='end'>
							<Button disabled={isLoading} onClick={submitPrompt} className={styles['send-cta']}>
								<Send />
							</Button>
						</InputAdornment>
					),
					sx: {
						alignItems: 'flex-end',
					},
				}}
			/>
		</div>
	)
}

export default AiChat
